import { createApi } from '@reduxjs/toolkit/query/react';
import { apiResource, b2chatBaseQuery } from '@src/client';
import { AssistantApiError, fetchAssistantApi } from '@src/types/assistant';
import env from '@src/utils/env';

/** Knowledge Base file of an agent: its vector store and the file `id`. */
export type KnowledgeFileArgs = {
  vectorStoreId: string;
  fileId: string;
};

/** `GET …/download-url` — the URL is valid for 60 s; use it right away. */
export type KnowledgeFileDownloadUrl = {
  url: string;
  expires_at: string;
  filename: string;
  size_bytes: number;
  content_type: string;
};

/**
 * HTTP status (0 when the request never got an answer) and the service's
 * `error` code. Decide on these; `message` is the service's English prose.
 */
export type KnowledgeFileError = {
  status: number;
  code?: string;
  message: string;
};

const fileUrl = ({ vectorStoreId, fileId }: KnowledgeFileArgs, path: string) =>
  `${env.WEB_SERVICE_ASSISTANTS_BASE_URI}/vector-stores/${encodeURIComponent(
    vectorStoreId
  )}/files/${encodeURIComponent(fileId)}/${path}`;

const toKnowledgeFileError = (err: unknown): KnowledgeFileError => {
  if (err instanceof AssistantApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return {
      status: err.status,
      code: body?.error,
      message: body?.message ?? err.message,
    };
  }
  return { status: 0, message: (err as Error)?.message ?? '' };
};

/**
 * The PDF the viewer embeds (ENG-2053): the bytes become a same-origin
 * `blob:` URL for the `<iframe>` and the "open in new tab" action. The Blob
 * type is fixed to `application/pdf` whatever the response advertised.
 */
const toPdfObjectUrl = (blob: Blob): string =>
  URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));

/**
 * One GET through `fetchAssistantApi` (JWT, service error envelope). The abort
 * signal RTK Query hands to the resource is forwarded, so closing the viewer
 * mid-download cancels the transfer; `responseType: 'blob'` keeps a binary
 * original (a PDF) as bytes instead of decoding it as text.
 */
const knowledgeFileRequest = (url: string, responseType?: 'blob') =>
  apiResource(async options => {
    try {
      const data = await fetchAssistantApi<
        string | Blob | KnowledgeFileDownloadUrl
      >(url, { method: 'GET', signal: options?.signal, responseType });
      // The resource contract is JSON-serialisable data: a Blob leaves here
      // already turned into its object URL.
      return {
        data: data instanceof Blob ? toPdfObjectUrl(data) : data,
        error: null,
      };
    } catch (err) {
      return { data: null, error: toKnowledgeFileError(err) };
    }
  }, {});

/**
 * Original files of the agents' Knowledge Base (F002, ia-assistant-service).
 * The backend answers both with `Cache-Control: no-store`: the content is not
 * kept once the viewer closes, and the download URL is never cached.
 */
const knowledgeFilesApi = createApi({
  reducerPath: 'knowledgeFilesApi',
  baseQuery: b2chatBaseQuery,
  endpoints: builder => ({
    // Plain text as uploaded; `fetchAssistantApi` returns `text()` for it.
    getKnowledgeFileContent: builder.query<string, KnowledgeFileArgs>({
      query: args => knowledgeFileRequest(fileUrl(args, 'content')),
      keepUnusedDataFor: 0,
    }),
    // The same endpoint for a `.pdf` (served as `application/pdf` since
    // ia-assistant-service#78). The cache holds the object URL, not the Blob,
    // and revokes it as soon as the viewer unsubscribes (`keepUnusedDataFor:
    // 0`). A tab opened from that URL keeps the document it already loaded;
    // reloading it afterwards fails by design.
    getKnowledgeFilePdfUrl: builder.query<string, KnowledgeFileArgs>({
      query: args => knowledgeFileRequest(fileUrl(args, 'content'), 'blob'),
      keepUnusedDataFor: 0,
      onCacheEntryAdded: async (
        _args,
        { cacheDataLoaded, cacheEntryRemoved }
      ) => {
        try {
          const { data } = await cacheDataLoaded;
          await cacheEntryRemoved;
          URL.revokeObjectURL(data);
        } catch {
          // The PDF never loaded (403/404/415/network): nothing to revoke.
        }
      },
    }),
    getKnowledgeFileDownloadUrl: builder.mutation<
      KnowledgeFileDownloadUrl,
      KnowledgeFileArgs
    >({
      query: args => knowledgeFileRequest(fileUrl(args, 'download-url')),
    }),
  }),
});

export const {
  useGetKnowledgeFileContentQuery,
  useGetKnowledgeFilePdfUrlQuery,
  useGetKnowledgeFileDownloadUrlMutation,
} = knowledgeFilesApi;

export default knowledgeFilesApi;
